import React from "react";
import { isCustomerLoggedIn } from "../../../../helpers/bycrypt";
import { Tooltip } from "antd";
import { RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT } from "../quotationTypes";
import placeHolderImage from "../../../../Assets/Images/placeholderImage.png";
import { PriceTypeLabels, ServicePriceType } from "../../Services/serviceTypes";
import {
  formatCurrency,
  formatToLocalDate,
  getFullAwsUrl,
  getThumbnailUrl,
} from "../../../../helpers/utils";
import { svg } from "../../../../Assets/svg";

const ServiceDetails = ({ data }: { data: any }) => {
  if (!data) return null;

  const serviceList = data.services || [];
  const servicesTotal = data.services_total || 0;
  const isRetailPriceVisible = !isCustomerLoggedIn() || data?.customer_snapshot?.show_retail_price !== false;
  const showHiddenRetailPriceTooltip = !isCustomerLoggedIn() && data?.customer_snapshot?.show_retail_price === false;

  // --- Render Single Service Item ---
  const renderServiceItem = (service: any, index: number) => {
    const pricingModel = service.pricing_model;

    // Logic to determine visible fields
    const isDayPerson =
      pricingModel === ServicePriceType.DAY_PERSON ||
      pricingModel === "per_day_per_person";
    const isDay =
      pricingModel === ServicePriceType.DAY || pricingModel === "per_day";
    const isCustom =
      pricingModel === ServicePriceType.CUSTOM || pricingModel === "custom";

    const showPerson = isDayPerson;
    const showDays = isDayPerson || isDay;
    const isFactoryAssembledFrames =
      service?.slug === "factory-assembled-frames" ||
      service?.service_id === "69649a195c96859b4ad602ac" ||
      service?.name?.toLowerCase() === "factory assembled frames";
    // Show Qty for standard items that aren't custom, person-based, or Factory Assembled Frames
    const showQty = !isCustom && !isDayPerson && !isFactoryAssembledFrames;

    const displayPriceType =
      PriceTypeLabels[service.pricing_model as ServicePriceType] ||
      service.pricing_model;

    return (
      <div key={service._id || index} className="card border rounded-3 mb-2">
        <div className="card-body py-3 px-3 fs-6 fw-semibold text-dark">
          <div className="row align-items-center gy-2">
            {/* Left Column: Image & Description (Takes 50%) */}
            <div className="col-md-6 col-12">
              <div className="d-flex align-items-center gap-2">
                <img
                  src={
                    service.image
                      ? getFullAwsUrl(service.image)
                      : placeHolderImage
                  }
                  width={50}
                  height={50}
                  className="rounded-2 object-fit-cover border img-60"
                  alt={service.name}
                />
                <div>
                  <h6 className="fw-bold mb-0 text-dark">{service.name}</h6>
                  {isRetailPriceVisible && (
                    <small className="text-muted fw-normal d-block">
                      {isCustom
                        ? "Custom Price"
                        : `${formatCurrency(service.unit_price, data?.currency)} / ${displayPriceType?.replace(
                            /_/g,
                            " ",
                          )}`}
                    </small>
                  )}
                  {service?.is_date_required && (
                    <small className="text-muted fw-normal d-block">
                      {service?.date_label
                        ? service?.date_label
                        : "Installation Date"}
                      :{" "}
                      {service.installation_date
                        ? formatToLocalDate(service.installation_date)
                        : "No Preference"}
                    </small>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Fixed Grid for Alignment (Takes 50%) */}
            <div className="col-md-6 col-12">
              <div className="row justify-content-md-end justify-content-between align-items-center gx-2">
                {/* Slot 1: Person OR Quantity */}
                <div className="col-3">
                  <div className="d-flex justify-content-end align-items-center gap-2">
                    {showPerson && (
                      <>
                        <small className="text-muted fw-normal text-nowrap">
                          Person:
                        </small>
                        <span className="fw-semibold text-dark">
                          {service.quantity}
                        </span>
                      </>
                    )}
                    {showQty && (
                      <>
                        <small className="text-muted fw-normal text-nowrap">
                          Qty:
                        </small>
                        <span className="fw-semibold text-dark">
                          {service.quantity}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Slot 2: Days */}
                <div className="col-3">
                  <div className="d-flex justify-content-end align-items-center gap-2">
                    {showDays && (
                      <>
                        <small className="text-muted fw-normal text-nowrap">
                          Days:
                        </small>
                        <span className="fw-semibold text-dark">
                          {service.days}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Slot 3: Total (Always visible) */}
                {isRetailPriceVisible && (
                  <div className="col-4">
                    <div className="d-flex justify-content-end align-items-start gap-1">
                      <div className="d-flex align-items-center gap-2">
                        <small className="text-muted fw-normal text-nowrap">
                          Total:
                        </small>
                        <span className="fw-bold text-dark text-nowrap">
                          {formatCurrency(service.line_total, data?.currency)}
                        </span>
                      </div>
                      {showHiddenRetailPriceTooltip && (
                        <Tooltip title={RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT}>
                          <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                            {svg?.restrictedAvatarIcon}
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-md-3 mb-2">
        <h4 className="fs-5 fw-bold text-black mb-0">Services</h4>
        <span className="fs-5">
          Services Total Amount:{" "}
          <span className="fw-bold">{formatCurrency(servicesTotal, data?.currency)}</span>
        </span>
      </div>

      <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white">
        {serviceList.length > 0 ? (
          <div className="d-flex flex-column gap-2">
            {serviceList.map((service: any, index: number) =>
              renderServiceItem(service, index),
            )}
          </div>
        ) : (
          <div className="text-center text-muted py-3 fst-italic">
            No services added.
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceDetails;
